import React, { useState, useMemo, useRef, useEffect } from 'react';
import { searchTimezones, getDetectedTimezone, TimezoneOption } from '../../utils/timezones';

interface TimezonePickerProps {
  value: string;
  onChange: (tz: string) => void;
  placeholder?: string;
}

export const TimezonePicker: React.FC<TimezonePickerProps> = ({
  value,
  onChange,
  placeholder = 'Search timezone...',
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const detected = useMemo(getDetectedTimezone, []);

  const results = useMemo(
    () => searchTimezones(query, detected).slice(0, 50),
    [query, detected],
  );

  useEffect(() => setHighlightIndex(0), [results]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (!listRef.current) return;
    const item = listRef.current.children[highlightIndex] as HTMLElement;
    item?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex]);

  const selectOption = (opt: TimezoneOption) => {
    onChange(opt.value);
    setQuery('');
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[highlightIndex]) {
      e.preventDefault();
      selectOption(results[highlightIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <input
        type="text"
        value={isOpen ? query : ''}
        placeholder={isOpen ? placeholder : value || placeholder}
        onFocus={() => setIsOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!isOpen) setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        style={{
          boxSizing: 'border-box',
          width: '100%',
          border: '1px solid #d4d4d4',
          borderRadius: '0.5rem',
          padding: '0.6rem 0.75rem',
          fontSize: '0.875rem',
          backgroundColor: 'white',
          outline: 'none',
        }}
      />
      {isOpen && results.length > 0 && (
        <div
          ref={listRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: '200px',
            overflowY: 'auto',
            background: 'white',
            border: '1px solid #d4d4d4',
            borderRadius: '0 0 0.5rem 0.5rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 100,
          }}
        >
          {results.map((opt, i) => (
            <div
              key={opt.value}
              onMouseDown={() => selectOption(opt)}
              onMouseEnter={() => setHighlightIndex(i)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '0.5rem 0.75rem',
                fontSize: '0.8rem',
                cursor: 'pointer',
                backgroundColor: i === highlightIndex ? '#f0f2f5' : 'transparent',
              }}
            >
              <span style={{ fontWeight: opt.value === detected ? 600 : 400 }}>
                {opt.label.replace(/_/g, ' ')}
                {opt.value === detected && (
                  <span style={{ fontSize: '0.7rem', color: '#065f46', marginLeft: '0.4rem' }}>
                    (detected)
                  </span>
                )}
              </span>
              <span style={{ color: '#888', fontSize: '0.75rem', flexShrink: 0, marginLeft: '1rem' }}>
                {opt.offset}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
